/**
 * pixel-layered の合成プレビュー(docs/19 §4.2。idle-0 の1枚だけ)。
 * 素材は公開版ルート `VITE_CONTENT_BASE` から読む(管理画面は R2 へ書かない)。
 * 整数倍の拡大のみ(`image-rendering: pixelated`)。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ItemDef } from '@paltyping/content-schema';
import {
  PIXEL_CELL,
  PREVIEW_FRAME,
  findFrame,
  pixelAssetUrl,
  pixelLayers,
} from '../../services/pixelCompose';

const imageCache = new Map<string, Promise<HTMLImageElement>>();
const atlasCache = new Map<string, Promise<unknown>>();

function loadImage(url: string): Promise<HTMLImageElement> {
  let cached = imageCache.get(url);
  if (!cached) {
    cached = new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        resolve(image);
      };
      image.onerror = () => {
        reject(new Error(url));
      };
      image.src = url;
    });
    cached.catch(() => imageCache.delete(url));
    imageCache.set(url, cached);
  }
  return cached;
}

function loadAtlas(url: string): Promise<unknown> {
  let cached = atlasCache.get(url);
  if (!cached) {
    cached = fetch(url).then(async (response) => {
      if (!response.ok) throw new Error(url);
      return (await response.json()) as unknown;
    });
    cached.catch(() => atlasCache.delete(url));
    atlasCache.set(url, cached);
  }
  return cached;
}

interface Props {
  readonly contentBase: string | null;
  readonly equipped: Readonly<Record<string, string | null | undefined>>;
  readonly itemsById: ReadonlyMap<string, ItemDef>;
  /** 拡大倍率(整数)。 */
  readonly scale?: number;
  readonly label?: string;
}

export function PixelPreview({ contentBase, equipped, itemsById, scale = 3, label }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const layers = useMemo(
    () => pixelLayers(equipped, itemsById, PREVIEW_FRAME),
    [equipped, itemsById],
  );
  const layersKey = JSON.stringify(layers);

  useEffect(() => {
    if (contentBase === null) return undefined;
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return undefined;
    let cancelled = false;
    setError(null);
    void Promise.all(
      layers.map(async (layer) => {
        const [image, atlas] = await Promise.all([
          loadImage(pixelAssetUrl(contentBase, layer.sheet)),
          loadAtlas(pixelAssetUrl(contentBase, layer.atlas)),
        ]);
        return { layer, image, rect: findFrame(atlas, PREVIEW_FRAME) };
      }),
    )
      .then((loaded) => {
        if (cancelled) return;
        context.clearRect(0, 0, PIXEL_CELL, PIXEL_CELL);
        context.imageSmoothingEnabled = false;
        for (const { image, rect } of loaded) {
          if (!rect) continue;
          context.drawImage(image, rect.x, rect.y, rect.w, rect.h, 0, 0, rect.w, rect.h);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [contentBase, layersKey]); // layers は layersKey で比べる

  if (contentBase === null) {
    return <p className="muted">VITE_CONTENT_BASE が未設定のため合成プレビューを出せません。</p>;
  }
  const size = PIXEL_CELL * Math.max(1, Math.round(scale));
  return (
    <div className="pixel-preview">
      <canvas
        ref={canvasRef}
        width={PIXEL_CELL}
        height={PIXEL_CELL}
        className="pixel-canvas"
        style={{ width: size, height: size }}
        aria-label={label ?? '合成プレビュー'}
      />
      {error !== null && <p className="muted">素材を読めませんでした: {error}</p>}
    </div>
  );
}
